<template>
  <div class="ppa-summary">
    <div class="ppa-summary__header eyebrow-label">
      <FontAwesomeIcon icon="list-check" class="ppa-summary__header-icon" />
      <span>{{ $t("ppa_summary.heading") }}</span>
    </div>

    <LoadingIndicatorComponent
      v-if="isLoading"
      inline
      class="ppa-summary__empty"
      :message="$t('ppa_summary.loading')"
    />

    <div v-else-if="rows.length === 0" class="ppa-summary__empty">
      {{ $t("ppa_summary.no_data") }}
    </div>

    <div v-else class="ppa-summary__rows">
      <div
        v-for="(row, index) in rows"
        :key="row.id"
        class="ppa-summary__row"
        :class="{ 'is-last': index === rows.length - 1 }"
      >
        <span class="ppa-summary__row-label">{{ row.label }}</span>
        <span class="ppa-summary__row-value">
          <template v-if="row.amountLabel">
            <span class="ppa-summary__row-date">{{ row.dateLabel }}</span>
            <span class="ppa-summary__row-sep">·</span>
            <span class="ppa-summary__row-amount">{{ row.amountLabel }}</span>
          </template>
          <template v-else>
            <span class="ppa-summary__row-date">{{ row.dateLabel }}</span>
          </template>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { format, parseISO } from "date-fns";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  calculateVolumeFromProjection,
  fetchProjectionData,
  findMatchingLimitForPeriod,
  getUpcomingExecutions,
} from "@/dashboard/production_strategy/overview/PPA/ppaExecutionUtils";
import type { PPAIndividualAllocation } from "@/dashboard/production_strategy/overview/useProductionAllocationData";
import type {
  ActionInstructions,
  PortfolioInstructions,
  RegistryDevice,
} from "@/types";

const props = defineProps<{
  companyId?: number | null;
  actionInstructions: ActionInstructions | null;
  portfolioInstructions: PortfolioInstructions | null;
  registryDevices: RegistryDevice[];
  individualAllocation?: PPAIndividualAllocation | null;
}>();

interface SummaryRow {
  id: string;
  label: string;
  dateLabel: string;
  amountLabel?: string;
}

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();
const rows = ref<SummaryRow[]>([]);
const isLoading = ref(false);
const projectionData = ref<Record<string, number> | null>(null);
const projectionRequestId = ref(0);

const resolvedCompanyId = computed<number | null>(() => {
  if (props.companyId != null) {
    return props.companyId;
  }
  return props.registryDevices?.[0]?.company_id ?? null;
});

const normalizedDeviceIds = computed(() => {
  const ids = props.portfolioInstructions?.registry_device_ids ?? [];
  return ids.map((id) => id.toString());
});

const deviceIdsKey = computed(() => normalizedDeviceIds.value.join("|"));

const numberFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

// `new Date("YYYY-MM-DD")` parses as UTC midnight, which renders as the
// previous day in west-of-UTC timezones. Route bare ISO dates through
// parseISO so the contract-end row etc. show the intended calendar day.
const formatShortDate = (date: Date | string | null | undefined) => {
  if (!date) return "";
  let d: Date;
  if (date instanceof Date) {
    d = date;
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    d = parseISO(date);
  } else {
    d = new Date(date);
  }
  if (Number.isNaN(d.getTime())) return "";
  return format(d, "d MMM yyyy");
};

const buildRows = () => {
  rows.value = [];

  const instructions = props.actionInstructions;
  if (!instructions) return;

  const upcoming = getUpcomingExecutions(instructions, 2);

  const allocatePercentage =
    props.portfolioInstructions?.allocate_percentage ?? 100;
  const projection = projectionData.value;
  const limits = props.individualAllocation?.limits ?? [];

  const rowLabels = [
    t("ppa_summary.next_delivery"),
    t("ppa_summary.following_delivery"),
  ];

  const upcomingRows: SummaryRow[] = upcoming.map((entry, index) => {
    const period =
      instructions.transaction_type === "CANCEL"
        ? entry.cancellationPeriod
        : entry.executionPeriod;
    let amount =
      projection && period
        ? calculateVolumeFromProjection(projection, period, allocatePercentage)
        : null;

    if (amount !== null && limits.length > 0 && period) {
      const matchingLimit = findMatchingLimitForPeriod(limits, period);
      if (matchingLimit) {
        const remaining =
          matchingLimit.limit_mwh - (matchingLimit.already_executed ?? 0);
        amount = Math.min(amount, Math.max(0, remaining));
      }
    }

    const amountLabel =
      amount == null
        ? t("ppa_summary.no_data_short")
        : t("ppa_summary.mwh_value", {
            amount: numberFormatter.format(amount),
          });

    return {
      id: `${entry.executionDate.toISOString()}-${index}`,
      label: rowLabels[index] ?? rowLabels[rowLabels.length - 1],
      dateLabel: formatShortDate(entry.executionDate),
      amountLabel,
    };
  });

  const next: SummaryRow[] = [...upcomingRows];

  if (instructions.end_date) {
    next.push({
      id: `contract-end-${instructions.end_date}`,
      label: t("ppa_summary.contract_ends"),
      dateLabel: formatShortDate(instructions.end_date),
    });
  }

  rows.value = next;
};

const refreshProjection = async () => {
  const currentRequest = ++projectionRequestId.value;
  projectionData.value = null;

  const companyId = resolvedCompanyId.value;
  const ids = normalizedDeviceIds.value;

  if (!companyId || ids.length === 0) {
    if (projectionRequestId.value === currentRequest) {
      isLoading.value = false;
    }
    return;
  }

  isLoading.value = true;

  try {
    const projection = await fetchProjectionData(companyId, ids);
    if (projectionRequestId.value === currentRequest) {
      projectionData.value = projection;
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    if (projectionRequestId.value === currentRequest) {
      isLoading.value = false;
    }
  }
};

watch(
  [() => resolvedCompanyId.value, () => deviceIdsKey.value],
  () => {
    void refreshProjection();
  },
  { immediate: true }
);

watch(
  [
    () => props.actionInstructions,
    () => props.portfolioInstructions?.registry_device_ids,
    () => props.portfolioInstructions?.allocate_percentage,
    () => props.companyId,
    () => props.registryDevices,
    () => props.individualAllocation,
    () => projectionData.value,
  ],
  () => {
    buildRows();
  },
  { immediate: true, deep: true }
);
</script>

<style scoped lang="scss">
.ppa-summary {
  padding: 16px 18px;
  background: linear-gradient(
    180deg,
    rgba(var(--phoenix-primary-rgb), 0.04),
    rgba(var(--phoenix-primary-rgb), 0.02)
  );
  border: 1px solid rgba(var(--phoenix-primary-rgb), 0.15);
  border-radius: 12px;
}

.ppa-summary__header {
  // Override eyebrow-label's inline-flex/gap defaults for this header that
  // spans the full row and wants more breathing room before the body.
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.ppa-summary__header-icon {
  color: var(--phoenix-primary);
  font-size: 12px;
}

.ppa-summary__empty {
  font-size: 13px;
  color: var(--phoenix-gray-600);
}

.ppa-summary__rows {
  display: flex;
  flex-direction: column;
}

.ppa-summary__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  border-bottom: 1px dashed rgba(var(--phoenix-primary-rgb), 0.15);
  font-size: 13px;
  color: var(--phoenix-gray-800);

  &.is-last {
    border-bottom: 0;
  }
}

.ppa-summary__row-label {
  flex: 0 0 auto;
}

.ppa-summary__row-value {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-variant-numeric: tabular-nums;
}

.ppa-summary__row-date {
  color: var(--phoenix-gray-900);
  font-weight: 600;
}

.ppa-summary__row-sep {
  color: var(--phoenix-gray-400);
  font-weight: 400;
}

.ppa-summary__row-amount {
  color: var(--phoenix-primary-darker, var(--phoenix-primary));
  font-weight: 700;
}
</style>
